import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  CircularProgress,
  Alert,
  Box,
  Chip
} from '@mui/material';
import { Person, Search, AdminPanelSettings } from '@mui/icons-material';
import apiService from '../services/api';

const UserImpersonationModal = ({ open, onClose, onImpersonate }) => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    if (open) {
      loadUsers();
    }
  }, [open]);

  useEffect(() => {
    // Filter users based on search term
    const filtered = users.filter(user => 
      user.FirstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.LastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.Email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredUsers(filtered);
  }, [users, searchTerm]);

  const loadUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiService.request('/users/all');
      if (response.success) {
        setUsers(response.data || []);
      } else {
        setError('Failed to load users');
      }
    } catch (error) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
  };

  const handleImpersonate = () => {
    if (selectedUser) {
      onImpersonate(selectedUser.Id);
      onClose();
    }
  };

  const handleClose = () => {
    setSelectedUser(null);
    setSearchTerm('');
    setError('');
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: 16,
          minHeight: '60vh',
          background: 'var(--color-bg-alt)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px var(--color-border)'
        }
      }}
    >
      <DialogTitle sx={{ 
        borderBottom: '1px solid var(--color-border)',
        pb: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        color: 'var(--color-text)'
      }}>
        <AdminPanelSettings style={{ color: 'var(--color-accent)' }} />
        <Typography component="span" variant="h6" style={{ color: 'var(--color-text)' }}>Impersonate User</Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search users by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <Search style={{ marginRight: 8, color: 'var(--color-text-secondary)' }} />
          }}
          sx={{ 
            mb: 2,
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: 'var(--color-border)',
              },
              '&:hover fieldset': {
                borderColor: 'var(--color-accent)',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'var(--color-accent)',
              },
              '& input': {
                color: 'var(--color-text)',
              },
              '& input::placeholder': {
                color: 'var(--color-text-secondary)',
                opacity: 1,
              },
              backgroundColor: 'var(--color-bg)',
            }
          }}
        />

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {filteredUsers.map((user) => (
              <ListItemButton
                key={user.Id}
                selected={selectedUser?.Id === user.Id}
                onClick={() => handleUserSelect(user)}
                sx={{
                  borderRadius: 1,
                  mb: 1,
                  border: selectedUser?.Id === user.Id ? '2px solid var(--color-accent)' : '1px solid var(--color-border)',
                  backgroundColor: selectedUser?.Id === user.Id ? 'var(--color-bg-hover)' : 'var(--color-bg)',
                  '&:hover': {
                    backgroundColor: 'var(--color-bg-hover)'
                  }
                }}
              >
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: 'var(--color-accent)' }}>
                    <Person style={{ color: 'white' }} />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle1" style={{ color: 'var(--color-text)' }}>
                        {user.FirstName} {user.LastName}
                      </Typography>
                      {user.Active === 0 && (
                        <Chip 
                          label="Inactive" 
                          size="small" 
                          color="error" 
                          variant="outlined"
                          sx={{
                            borderColor: 'var(--color-error)',
                            color: 'var(--color-error)'
  }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Typography variant="body2" style={{ color: 'var(--color-text-secondary)' }}>
                      {user.Email}
                    </Typography>
                  }
                />
              </ListItemButton>
            ))}
            {filteredUsers.length === 0 && !loading && (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
                <Typography style={{ color: 'var(--color-text-secondary)' }}>
                  {searchTerm ? 'No users found matching your search' : 'No users available'}
                </Typography>
              </Box>
            )}
          </List>
        )}

        {selectedUser && (
          <Box mt={2} p={2} sx={{ 
            backgroundColor: 'var(--color-bg)', 
            borderRadius: 1,
            border: '1px solid var(--color-border)'
  }}>
            <Typography variant="subtitle2" style={{ color: 'var(--color-accent)' }} gutterBottom>
              Selected User:
            </Typography>
            <Typography variant="body1" style={{ color: 'var(--color-text)' }}>
              {selectedUser.FirstName} {selectedUser.LastName}
            </Typography>
            <Typography variant="body2" style={{ color: 'var(--color-text-secondary)' }}>
              {selectedUser.Email}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ 
        borderTop: '1px solid var(--color-border)',
        pt: 2,
        px: 3,
        pb: 3,
        gap: 2
  }}>
        <Button 
          onClick={handleClose} 
          variant="outlined"
          sx={{
            color: 'var(--color-text-secondary)',
            borderColor: 'var(--color-border)',
            '&:hover': {
              backgroundColor: 'var(--color-bg-hover)',
              borderColor: 'var(--color-accent)'
  }
          }}
        >
          Cancel
        </Button>
        <button
          onClick={handleImpersonate}
          disabled={!selectedUser}
          style={{
            padding: '10px 22px',
            borderRadius: '8px',
            border: 'none',
            background: 'linear-gradient(135deg, var(--color-accent), var(--color-accent2))',
            color: 'white',
            fontWeight: 600,
            fontSize: '15px',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            transition: 'background 0.2s',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
  }}
          onMouseEnter={(e) => {
            if (!selectedUser) return;
            e.target.style.background = 'linear-gradient(135deg, var(--color-accent2), var(--color-accent))';
          }}
          onMouseLeave={(e) => {
            if (!selectedUser) return;
            e.target.style.background = 'linear-gradient(135deg, var(--color-accent), var(--color-accent2))';
          }}
        >
          <AdminPanelSettings style={{ fontSize: '20px' }} />
          Impersonate User
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default UserImpersonationModal; 




